import * as React from 'react'
import { Loader2, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'

interface GrantCodeFieldProps {
  email: string
  value: string
  onChange: (code: string) => void
  onSend: () => Promise<void>
  error?: string | undefined
}

/** The code PixelSign emails before granting live or staff-change access: send it, then type it in. */
export function GrantCodeField({ email, value, onChange, onSend, error }: GrantCodeFieldProps) {
  const [sending, setSending] = React.useState(false)
  const [sent, setSent] = React.useState(false)

  async function send(): Promise<void> {
    setSending(true)
    try {
      await onSend()
      setSent(true)
      toast.success(`Code sent to ${email}`)
    } catch (failure) {
      toast.error(apiErrorMessage(failure, 'Could not send the code.'))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-2">
      <Button type="button" variant="secondary" onClick={() => void send()} disabled={sending}>
        {sending ? <Loader2 className="animate-spin" /> : <Mail />}
        {sent ? 'Resend code' : 'Email me a code'}
      </Button>
      <Field
        label="Code from your email"
        htmlFor="grant-code"
        hint={`Live or staff access needs a code sent to ${email}. It expires in 10 minutes.`}
        error={error}
      >
        <Input
          id="grant-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          className="font-mono tracking-widest"
          value={value}
          aria-invalid={error ? true : undefined}
          aria-describedby={fieldMessageId('grant-code')}
          onChange={(event) => onChange(event.target.value.replace(/\D/g, ''))}
        />
      </Field>
    </div>
  )
}
